import { create } from "zustand";

interface PendingChallenge {
  resolve: () => void;
  reject: (error: Error) => void;
}

interface StepUpState {
  /** Whether the {@link StepUpDialog} should be visible. */
  isOpen: boolean;
  /**
   * Promise resolvers for every caller currently awaiting this challenge.
   * Concurrent 428s (e.g. a burst of admin actions) share ONE dialog — the
   * first caller opens it, later callers while it's open just add their
   * resolver to this list and get the same outcome.
   */
  pending: PendingChallenge[];
}

interface StepUpActions {
  /** Register a new pending challenge, opening the dialog. */
  enqueue: (challenge: PendingChallenge) => void;
  /**
   * The admin successfully re-confirmed their password (StepUpDialog calls
   * this after a successful `supabase.auth.signInWithPassword`). Resolves
   * every pending caller and closes the dialog.
   */
  resolveChallenge: () => void;
  /**
   * The admin cancelled (closed the dialog / backdrop / Escape) — reject
   * every pending caller so `api.ts`'s interceptor re-surfaces the ORIGINAL
   * 428 error rather than retrying.
   */
  cancelChallenge: () => void;
}

/**
 * Ephemeral, client-only store bridging the axios response interceptor
 * (`api.ts`) and the {@link StepUpDialog} component (issue #1402, porting
 * `apps/web`'s #498 Phase A pattern to the admin console — the ONLY client
 * that calls platform-admin routes, so there is no carve-out to lean on
 * here). The interceptor calls {@link requestStepUp} and awaits the returned
 * promise; the dialog reads `isOpen` to render itself and calls
 * `resolveChallenge`/`cancelChallenge` on submit/close.
 */
export const useStepUpStore = create<StepUpState & StepUpActions>((set, get) => ({
  isOpen: false,
  pending: [],
  enqueue: (challenge) => {
    set((state) => ({ isOpen: true, pending: [...state.pending, challenge] }));
  },
  resolveChallenge: () => {
    const { pending } = get();
    set({ isOpen: false, pending: [] });
    for (const challenge of pending) {
      challenge.resolve();
    }
  },
  cancelChallenge: () => {
    const { pending } = get();
    set({ isOpen: false, pending: [] });
    const error = new Error("Step-up re-authentication was cancelled");
    for (const challenge of pending) {
      challenge.reject(error);
    }
  },
}));

/**
 * Request a step-up re-authentication challenge (#1402). Opens the
 * {@link StepUpDialog} (or joins the one already open) and returns a promise
 * that resolves once the admin successfully re-confirms their password, or
 * rejects if they cancel.
 * @returns A promise settling on the outcome of the challenge.
 */
export function requestStepUp(): Promise<void> {
  return new Promise((resolve, reject) => {
    useStepUpStore.getState().enqueue({ resolve, reject });
  });
}
